import { Box } from '@mui/material';
import { Fragment, useState } from 'react';
import { neutral } from '@ahora-patagonia/design-tokens';
import {
  NOTAS_POR_PASO,
  type NotaPublicaCard,
  type PublicAds,
} from '@ahora-patagonia/contracts';
import { FRANJA } from '../../../../theme.js';
import { EspacioPublicitario } from '../../../shared/ads/EspacioPublicitario.js';
import { intercaladosEnLista } from '../../../shared/ads/cadencia.js';
import { FilaNotaMiniatura } from '../../../shared/notas/FilaNotaMiniatura.js';
// Las dos secciones que no son categorías: su clave **es** su ruta, así que el "Ver todo" sale de
// la misma constante que registra la página y no de un string suelto acá.
import { SECCION_KIND } from '../../../seccion/models/entities/SeccionData.js';
import { CarruselDelBloque } from './CarruselDelBloque.js';
import { EncabezadoBloque } from './EncabezadoBloque.js';
import { PlantillaCuadricula } from './PlantillaCuadricula.js';
import { PlantillaLista } from './PlantillaLista.js';
import { PlantillaTira } from './PlantillaTira.js';
import { Portada } from './Portada.js';
import { enPaginas } from './paginas.js';
import type { PartePortada } from './portadaPartes.js';
import type { Plantilla, Presentacion } from './rotacion.js';

/**
 * Cuántas notas de Última hora se ven **en el cuerpo** (el corte sin columna lateral).
 *
 * El servidor manda hasta `PUBLIC_ULTIMA_HORA_TOPE` (30), que es lo que necesita la columna lateral
 * de escritorio: una tira larga que acompaña toda la página. Apiladas en un teléfono esas 30 son
 * una pantalla y media de titulares sueltos, con sus avisos intercalados, entre una sección y otra.
 *
 * Cuatro es un vistazo, no un listado: alcanza para saber si pasó algo y para que "Ver todo" —que
 * ya está en el encabezado— sea el camino natural a la lista completa. De paso, con cuatro la
 * cadencia interna de avisos (uno cada cinco) no llega a dispararse, así que el bloque queda limpio
 * sin tener que apagar nada.
 */
const ULTIMA_HORA_EN_EL_CUERPO = 4;

export interface BloqueProps {
  presentacion: Presentacion;
  ads: PublicAds;
  /**
   * Qué mitad de la portada dibuja este render. Solo lo manda la home, y solo importa en las
   * maquetas B y C, cuyo frente corre por encima de la columna lateral (ver `portadaPartes.ts`).
   */
  partePortada?: PartePortada;
}

/**
 * El corazón de la home: recibe un bloque ya resuelto y su presentación (plantilla +
 * ground, calculadas por `asignarPresentacion`) y lo dibuja. La home lo itera **sin
 * conocer nombres de categorías**: agregar una categoría en el panel no toca ninguna
 * pantalla.
 *
 * El ground de franja sangra hasta el borde del contenido (márgenes negativos del padding
 * de página) para que la alternancia se lea como franjas, no como cajas.
 */
export function Bloque({ presentacion, ads, partePortada = 'completa' }: BloqueProps) {
  const { bloque, plantilla, ground, numerada } = presentacion;

  const contenido = (() => {
    if (bloque.kind === 'PORTADA') {
      // Sin encabezado: la portada es lo primero de la página y su jerarquía ya la dan la
      // foto grande y el titular. Un rótulo "Portada" arriba solo repite lo obvio.
      //
      // PREVIEW: el pie de portada está apagado para evaluar la portada sin publicidad.
      // Para volver a venderlo, acá va `ads.IN_FEED` (es el mismo camino que sigue el sitio
      // cuando esa ubicación no tiene campaña activa).
      return (
        <Portada
          slots={bloque.slots}
          maqueta={bloque.maqueta}
          parte={partePortada}
          adPieDePortada={undefined}
        />
      );
    }

    if (bloque.kind === 'ULTIMA_HORA') {
      // En el cuerpo (<1200) es el repaso del día al pie de la home, recortado a unas pocas notas:
      // la lista completa está a un clic, en "Ver todo". En escritorio este bloque vive en la
      // columna lateral y el del cuerpo se oculta por CSS (ver HomeView), que es donde sí se usan
      // las 30 que manda el servidor.
      const notas = bloque.notas.slice(0, ULTIMA_HORA_EN_EL_CUERPO);
      const conAd = intercaladosEnLista(notas.length);
      return (
        <>
          <EncabezadoBloque nombre={bloque.name} verTodo={`/${SECCION_KIND.ultimaHora}`} />
          <Box>
            {notas.map((nota, i) => (
              <Fragment key={nota.id}>
                <Box sx={{ py: 1.5, ...(i > 0 ? { borderTop: `1px solid ${neutral[300]}` } : {}) }}>
                  <FilaNotaMiniatura nota={nota} />
                </Box>
                {conAd.has(i) && (
                  <Box sx={{ py: 1 }}>
                    <EspacioPublicitario ubicacion="IN_FEED" ad={ads.IN_FEED} />
                  </Box>
                )}
              </Fragment>
            ))}
          </Box>
        </>
      );
    }

    if (bloque.kind === 'MAS_LEIDAS') {
      // Mismo mecanismo que un bloque de categoría —el editor elige disposición y cantidad, y con
      // más de una página aparecen los chevrones—, con una diferencia: va numerado.
      return (
        <BloquePaginado
          nombre={bloque.name}
          notas={bloque.notas}
          plantilla={plantilla}
          elegida={bloque.plantilla !== null}
          numerada={numerada}
          verTodo={`/${SECCION_KIND.masLeidas}`}
        />
      );
    }

    return (
      <BloquePaginado
        nombre={bloque.name}
        notas={bloque.notas}
        plantilla={plantilla}
        elegida={bloque.plantilla !== null}
        verTodo={`/${bloque.categoryUrlKey}`}
      />
    );
  })();

  return (
    <Box
      component="section"
      sx={{
        py: 2.5,
        ...(ground === 'franja'
          ? {
              backgroundColor: FRANJA,
              // El sangrado llega hasta el borde de la página, pero por la derecha solo
              // mientras no haya lateral: en ≥1200 la franja termina en el borde de la
              // columna y deja el gutter en blanco, si no el gris toca la tira de Última hora.
              ml: { xs: '-14px', sm: '-20px', lg: '-32px' },
              mr: { xs: '-14px', sm: '-20px', lg: 0 },
              pl: { xs: '14px', sm: '20px', lg: '32px' },
              // En ≥1200 el margen derecho es 0 (la franja no invade el gutter), pero el padding
              // **no** puede ser 0: sin él el contenido queda flush contra el borde del gris y
              // "Ver todo" termina pegado a la tira de Última hora, sin aire. Se mantiene el
              // mismo inset que a la izquierda para que la tira quede centrada en su franja.
              pr: { xs: '14px', sm: '20px', lg: '32px' },
            }
          : {}),
      }}
    >
      {contenido}
    </Box>
  );
}

/**
 * Un bloque que es una lista de notas —una categoría o Más leídas—, con su paginado si el editor
 * pidió más notas de las que entran de una vez. Los dos se configuran igual, así que se dibujan con
 * el mismo componente.
 *
 * "Paginado" es el caso de la tira y la lista. La **cuadrícula** entra por el mismo lugar porque se
 * configura igual —disposición y cantidad, en el mismo popover del panel— pero se dibuja sin páginas:
 * todas sus notas en una grilla que crece hacia abajo.
 *
 * La página es **estado local**: las notas ya vinieron todas en la respuesta de la home. Arranca en
 * la primera tanto en el server como en el cliente, así la hidratación no encuentra diferencias.
 *
 * El bloque **sin preferencia guardada** se dibuja como siempre —una sola página, sin chevrones y
 * sin riel—: el paginado es parte de lo que el editor eligió, y una portada que nadie tocó no
 * cambia de aspecto por esto.
 */
function BloquePaginado({
  nombre,
  notas,
  plantilla,
  elegida,
  verTodo,
  numerada = false,
}: {
  nombre: string;
  notas: NotaPublicaCard[];
  plantilla: Plantilla;
  /** El editor eligió disposición: es lo que habilita el paginado. */
  elegida: boolean;
  verTodo?: string;
  numerada?: boolean;
}) {
  const [pagina, setPagina] = useState(0);

  // La cuadrícula sale antes del paginado: no tiene páginas que armar ni riel que correr, así que
  // pasar por `enPaginas` sería partir en tandas de 3 una grilla que se dibuja de una sola vez.
  if (plantilla === 'D') {
    return (
      <>
        <EncabezadoBloque nombre={nombre} {...(verTodo ? { verTodo } : {})} />
        <PlantillaCuadricula notas={notas} numerada={numerada} />
      </>
    );
  }

  const porPagina = plantilla === 'B' ? NOTAS_POR_PASO.TIRA : NOTAS_POR_PASO.LISTA;
  const paginas = elegida ? enPaginas(notas, porPagina) : [notas];
  // La numeración es continua entre páginas: la segunda arranca donde terminó la primera, porque
  // "1" en la página 2 diría que hay dos notas más leídas.
  const dibujar = (deLaPagina: NotaPublicaCard[], indice: number) => {
    const inicio = indice * porPagina + 1;
    return plantilla === 'B' ? (
      <PlantillaTira notas={deLaPagina} numerada={numerada} inicio={inicio} />
    ) : (
      <PlantillaLista notas={deLaPagina} numerada={numerada} inicio={inicio} />
    );
  };

  return (
    <>
      <EncabezadoBloque nombre={nombre} {...(verTodo ? { verTodo } : {})} />
      {paginas.length > 1 ? (
        <CarruselDelBloque
          paginas={paginas.map((deLaPagina, i) => dibujar(deLaPagina, i))}
          // Todas las páginas viven en el riel, pero el índice sigue acotado: un bloque que
          // encoge entre renders no puede dejar el riel corrido a una página que ya no está.
          pagina={Math.min(pagina, paginas.length - 1)}
          onCambiar={setPagina}
        />
      ) : (
        dibujar(paginas[0] ?? [], 0)
      )}
    </>
  );
}
